import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { CircleNotch, EnvelopeSimple } from "@phosphor-icons/react";
import { logger } from "../services/telemetry";
import { useAuthMethods } from "./session";
import { trackButtonPressed } from "../analytics/tracking";
import { passwordResetErrorMessage } from "./authErrors";
import { LOGIN_PATH } from "./paths";
import {
  AuthCard,
  AuthCta,
  AuthField,
  AuthHeading,
  AuthFrame,
  EMAIL_RE,
} from "./AuthShared";

/* Request a password-reset email. Reset runs entirely through Firebase — the
 * identity service has no reset endpoint — so this screen only collects the
 * email and triggers sendPasswordResetEmail; Firebase emails the link and hosts
 * the new-password page. On success we show a generic "if an account exists…"
 * confirmation regardless of whether the address is registered (Firebase's
 * email-enumeration protection sends nothing for unknown addresses), so the
 * screen can't be used to probe which emails exist. */

export function ForgotPasswordScreen() {
  const { sendPasswordReset } = useAuthMethods();
  const [email, setEmail] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const validate = () => {
    const trimmed = email.trim();
    const e = !trimmed
      ? "Enter your email."
      : !EMAIL_RE.test(trimmed)
        ? "Enter a valid email address."
        : "";
    setEmailErr(e);
    return !e;
  };

  const handleSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;

    trackButtonPressed("Send reset link");
    setFormErr(null);
    setLoading(true);
    try {
      await sendPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      logger.error(
        err instanceof Error ? err : new Error(String(err)),
        "Password reset request failed",
      );
      setFormErr(passwordResetErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthFrame>
      <div className="onboarding onboarding-card w-full">
        <AuthCard>
          {sent ? (
            <div className="flex flex-col items-center gap-6 text-center">
              <div
                className="flex size-12 items-center justify-center [&_svg]:size-6"
                style={{
                  background: "var(--petal)",
                  color: "var(--carmine)",
                  borderRadius: "var(--radius-pill)",
                }}
              >
                <EnvelopeSimple />
              </div>
              <div className="flex flex-col gap-1.5">
                <AuthHeading>Check your email</AuthHeading>
                <p className="t-body-sm">
                  If an account exists for{" "}
                  <span style={{ color: "var(--fg-1)", fontWeight: 600 }}>
                    {email.trim()}
                  </span>
                  , we sent a link to reset your password. It may take a minute
                  to arrive — check your spam folder too.
                </p>
              </div>
              <Link
                to={LOGIN_PATH}
                className="t-body-sm font-medium no-underline hover:underline"
                style={{ color: "var(--fg-1)" }}
                onClick={() => trackButtonPressed("Back to sign in")}
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-1.5">
                <AuthHeading>Reset your password</AuthHeading>
                <p className="t-body-sm">
                  Enter your email and we&apos;ll send you a link to set a new
                  password.
                </p>
              </div>

              {formErr && (
                <div
                  role="alert"
                  className="rounded-md border border-error-border bg-error-bg px-3 py-2.5"
                >
                  <span className="text-body-sm text-error-fg">{formErr}</span>
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
                noValidate
              >
                <AuthField
                  id="email"
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(v) => {
                    setEmail(v);
                    if (emailErr) setEmailErr("");
                  }}
                  error={emailErr}
                  placeholder="you@company.com"
                  autoComplete="email"
                  disabled={loading}
                  autoFocus
                />
                <AuthCta
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  iconLeft={
                    loading ? (
                      <CircleNotch className="animate-spin" />
                    ) : undefined
                  }
                >
                  Send reset link
                </AuthCta>
              </form>

              <p
                className="text-center text-[12px]"
                style={{ color: "var(--fg-3)" }}
              >
                Remembered it?{" "}
                <Link
                  to={LOGIN_PATH}
                  className="font-medium no-underline hover:underline"
                  style={{ color: "var(--fg-1)" }}
                  onClick={() => trackButtonPressed("Back to sign in")}
                >
                  Back to sign in
                </Link>
              </p>
            </div>
          )}
        </AuthCard>
      </div>
    </AuthFrame>
  );
}
